import { router } from '@inertiajs/react';
import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { ArrowLeft, ArrowRight, Calendar, Globe, Heart, MapPin } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';

import { prettifyUrl } from '../../Shared/helpers';
import { useLocale } from '../../Shared/locale';
import { Charity, Event, PublicCampaign, PublicProject, SimplePagination } from '../../Shared/types';
import DateRange from '../../Shared/UI/DateRange';
import { Icon } from '../../Shared/UI/Icon';
import Picker, { PickerItem } from '../../Shared/UI/Picker';
import { SearchFilterInput } from '../../Shared/UI/SearchFilterInput';
import SupportaSymbol from '../../Shared/UI/SupportaSymbol';
import { useCharityStyle } from '../../Shared/useCharityStyle';
import useScroll from '../../Shared/useScroll';
import { useRouter } from '../router';
import { SharedProps } from '../types';
import { LinkButton as CharityLinkButton, LinkButton } from '../UI/Button';
import Container from '../UI/Container';
import { Footer } from '../UI/Footer';
import Layout from '../UI/Layout';
import { NavBar } from '../UI/NavBar';
import { Background } from './Background';
import CampaignCard from './CampaignCard';
import { CharityBanner } from './CharityBanner';
import Content from './Content';
import { Page } from './ContentPage';

export interface HomePageProps extends SharedProps {
  campaigns: SimplePagination<PublicCampaign & {
    project: PublicProject;
    event: Event | null;
    charity: Charity;
  }>;
  campaignsCount: number;
  fundsRaised: number;
  charities: (PickerItem & {
    description: string | null;
    logo_url: string | null;
    slug: string;
  })[];
  events: (PickerItem & { slug: string; })[];
  popularEvents: Event[];
  charity: Charity | null;
  event: Event | null;
  page: Page | null;
}

export default function HomePage({
  campaigns,
  campaignsCount,
  fundsRaised,
  charities,
  events,
  popularEvents,
  charity,
  event,
  contentNav,
  page,
  auth: {
    user,
  },
  params,
}: HomePageProps) {
  const { t } = useTranslation();
  const { locale, formatCurrency, formatNumber } = useLocale();
  const { routes } = useRouter();
  const { scrollToTop } = useScroll();

  useCharityStyle(charity);

  const [searchInputFocused, setSearchInputFocused] = useState(false);

  const getOverviewUrl = (charityId?: string | null, eventId?: string | null) => {
    const search = params.search ? `?search=${params.search}` : '';

    if (eventId) {
      return `/${locale}/fundraisers/${charityId || 'all'}/${eventId}${search}`;
    }

    if (charityId) {
      return `/${locale}/fundraisers/${charityId}${search}`;
    }

    return `/${locale}${search}`;
  };

  return (
    <>
      <NavBar navItems={contentNav} user={user} />

      <Background />

      <div className="relative z-2">
        <Container size="lg" spacing="lg">
          <div className="text-center">
            <div className="block text-base text-slate-500 font-medium mb-2">
              {t('frontend:homepage.your_platform_for')}
            </div>

            <h1 className="text-3xl text-center text-balance mb-4 font-semibold">
              {charity && (
                <Trans i18nKey="frontend:homepage.campaigns_for_specific_charity" values={{ charity: charity.title }}>
                  <strong className="font-extrabold" />
                </Trans>
              )}
              {!charity && (
                <Trans i18nKey="frontend:homepage.campaigns_for_charity">
                  <strong className="font-extrabold" />
                </Trans>
              )}
            </h1>

            <div>
              <CharityLinkButton
                href={charity ? routes.create_campaign_page(charity.id) : routes.select_charity_page()}
              >
                {t('frontend:start_campaign')}
              </CharityLinkButton>
            </div>
          </div>

          <div className="mx-auto space-y-4 max-w-screen-lg sm:px-8">
            <div className="grid sm:grid-cols-3 space-y-2 sm:space-y-0 sm:flex-row sm:justify-center w-full sm:rounded-xl sm:shadow-[0_1px_2px_0_rgb(0_0_0_/_0.05),0_10px_15px_-3px_rgb(0_0_0_/_0.05),_0_2px_4px_-2px_rgb(0_0_0_/_0.05)] mx-auto">
              <div className={classNames(
                'relative',
                searchInputFocused && 'z-50'
              )}>
                <SearchFilterInput
                  inputSize="lg"
                  className="sm:rounded-l-xl sm:rounded-r-none"
                  onFocus={() => setSearchInputFocused(true)}
                  onBlur={() => setSearchInputFocused(false)}
                  aria-label={t('shared:filter.search')}
                />
              </div>
              <div className="sm:ml-[-1px]">
                <Picker
                  onChange={(item) => router.get(getOverviewUrl(item?.slug, event?.slug))}
                  items={charities.map((charity) => ({
                    ...charity,
                    search: charity.description,
                  }))}
                  selected={charity ? charity.id : null}
                  icon={<Heart />}
                  optional
                  placeholder={(t('frontend:homepage.n_charities', {
                    count: charities.length,
                    value: formatNumber(charities.length),
                  }))}
                  inputSize="lg"
                  className="sm:rounded-none"
                  aria-label={t('frontend:homepage.select_a_charity')}
                />
              </div>
              <div className="sm:ml-[-1px]">
                <Picker
                  onChange={(item) => router.get(getOverviewUrl(charity?.slug, item?.slug))}
                  items={events}
                  selected={event ? event.id : null}
                  icon={<Calendar />}
                  optional
                  placeholder={(t('frontend:homepage.n_events', {
                    count: events.length,
                    value: formatNumber(events.length),
                  }))}
                  inputSize="lg"
                  className="sm:rounded-l-none sm:rounded-r-xl"
                  aria-label={t('frontend:homepage.select_an_event')}
                />
              </div>
            </div>
          </div>

          {charity && (
            <div className="bg-gradient-to-b from-project/10 to-project/5 rounded-xl">
              <div className="grid md:grid-cols-3 gap-0 md:gap-8 rounded-xl items-center">
                <div
                  className={classNames(
                    'md:col-span-2 p-6',
                    charity.logo_url && 'grid sm:grid-cols-[120px_1fr] gap-6 items-center',
                  )}
                >
                  {charity.logo_url && (
                    <div className="bg-white shadow-sm rounded-lg py-6 px-4 flex justify-center items-center shrink-0 sm:h-full">
                      <img src={charity.logo_url} alt={charity.title} className="max-h-12 sm:max-h-16" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-lg font-bold">
                      {charity.title}
                    </h2>
                    {charity.description}
                    {charity.website && (
                      <p>
                        <a href={charity.website} target="_blank" rel="noopener noreferrer">
                          <Icon className="mr-1">
                            <Globe />
                          </Icon>
                          {prettifyUrl(charity.website)}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
                <div className="w-full p-6 md:p-8 border-t md:border-t-0 md:border-l border-project/10 h-full">
                  <div className="grid grid-cols-2 mb-3 h-full items-center">
                    <div>
                      <strong className="text-lg whitespace-nowrap">
                        {formatCurrency(fundsRaised, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </strong>
                      <br />
                      {t('frontend:raised')}
                    </div>
                    <div>
                      <strong className="text-lg">
                        {formatNumber(campaignsCount)}
                      </strong>
                      <br />
                      {t('frontend:campaigns', { count: campaignsCount })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {event && (
            <div className="flex items-center flex-wrap md:justify-between text-sm text-slate-500">
              <div className="flex items-center justify-center w-full md:w-auto">
                {event.logo_url && (
                  <div className="w-10 h-10 rounded-full bg-white mr-3">
                    <img src={event.logo_url || ''} alt={event.title} className="object-contain h-full w-full rounded-full" />
                  </div>
                )}

                <h3 className="text-base font-bold">
                  {event.title}
                </h3>
              </div>

              <div className="flex flex-wrap justify-center w-full md:w-auto">
                <div className="mt-2 mx-2 md:ml-4 md:mr-0">
                  <Icon className="mr-1">
                    <Calendar />
                  </Icon>
                  <span>
                    <DateRange start={event.start_date} end={event.end_date} />
                  </span>
                </div>
                {event.location && (event.location.id || event.location.description) && (
                  <div className="mt-2 mx-2 md:ml-4 md:mr-0">
                    <Icon className="mr-1">
                      <MapPin />
                    </Icon>
                    <span>
                      {event.location.description.split(',')[0]}
                    </span>
                  </div>
                )}
                {event.website && (
                  <div className="mt-2 mx-2 md:ml-4 md:mr-0">
                    <a href={event.website} target="_blank" rel="noopener noreferrer" className="text-slate-500">
                      <Icon className="mr-1">
                        <Globe />
                      </Icon>
                      {prettifyUrl(event.website)}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {campaigns.data.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {campaigns.data.map((campaign) => (
                <CampaignCard campaign={campaign} key={campaign.id} />
              ))}
            </div>
          )}

          {campaigns.data.length === 0 && (
            <div className="text-center text-xl font-semibold py-16">
              <SupportaSymbol className="text-slate-200 w-12 mx-auto mb-4" />
              {t('frontend:no_results')}
            </div>
          )}

          {(campaigns.meta.current_page > 1 || campaigns.meta.has_more_pages) && (
            <div className={classNames('flex justify-center items-center space-x-4')}>
              <LinkButton
                href={campaigns.links.prev || ''}
                preserveState
                variant="tertiary"
                size="xs"
                disabled={!campaigns.links.prev}
                className="!font-medium !text-slate-900"
                onClick={() => scrollToTop()}
              >
                <Icon className="mr-1">
                  <ArrowLeft />
                </Icon>
                {t('shared:pager.prev')}
              </LinkButton>

              <span className="text-slate-400 text-sm">
                {t('shared:pager.page_n', { page: formatNumber(campaigns.meta.current_page) })}
              </span>

              <LinkButton
                href={campaigns.links.next || ''}
                preserveState
                variant="tertiary"
                size="xs"
                disabled={!campaigns.links.next}
                className="!font-medium !text-slate-900"
                onClick={() => scrollToTop()}
              >
                {t('shared:pager.next')}
                <Icon className="ml-1">
                  <ArrowRight />
                </Icon>
              </LinkButton>
            </div>
          )}
        </Container>

        {page && (
          <>
            <hr className="border-slate-150" />
            <Content content={page.content} charities={charities} popularEvents={popularEvents} />
          </>
        )}

        <hr className="border-slate-150" />

        <Container size="lg" spacing="lg">
          <CharityBanner />
        </Container>

        <Footer links={contentNav} />
      </div>
    </>
  );
}

HomePage.layout = (page: ReactNode) => <Layout>{page}</Layout>;
